/*
Note: Column height diff and balancing is currently implemented with a 2-col
layout in mind. If more columns are needed, the logic for both of the above will need 
to be updated. For best results, calculate the diffs between each col & set parallax
speeds accordingly.
*/

import { type RefObject, useEffect, useState } from "react"

import { useMatchMedia } from "~/hooks/useMatchMedia"

type TItem = {
  node: HTMLElement
  height: number
}

type MasonryOptions = {
  container: HTMLElement
  columns: number
  onLayout?: (heightDiff: number) => void
  balanceCols?: boolean
}

class MasonryConstructor {
  private container: HTMLElement
  private columns: number
  private onLayout?: (heightDiff: number) => void
  private balanceCols?: boolean

  items: TItem[] | null = null
  cols: TItem[][]
  resizeObserver: ResizeObserver
  mutationObserver: MutationObserver

  constructor({ container, columns, onLayout, balanceCols }: MasonryOptions) {
    this.container = container
    this.columns = columns
    this.onLayout = onLayout
    this.balanceCols = balanceCols

    this.cols = []
    this.resizeObserver = new ResizeObserver(this.redraw)
    this.resizeObserver.observe(this.container)
    this.mutationObserver = new MutationObserver(this.redraw)
    this.mutationObserver.observe(this.container, {
      childList: true,
    })
  }

  private getItems() {
    return (
      Array.from(
        this.container.querySelectorAll(".masonry-item"),
      ) as HTMLElement[]
    ).map((node) => ({ node, height: node.clientHeight }))
  }

  private getCols() {
    if (!this.items) return []

    const cols: TItem[][] = [...Array(this.columns)].map(() => [])

    this.items.forEach((item, idx) => {
      let columnIndex = idx % this.columns

      if (this.balanceCols) {
        const colHeights = this.getColHeights(cols)

        columnIndex = colHeights.indexOf(Math.min(...colHeights))
      }

      item.node.setAttribute("data-column", columnIndex.toString())

      const column = cols[columnIndex]
      if (!column) return
      column.push(item)
    })

    return cols
  }

  private setItemPositions() {
    this.cols.forEach((col) => {
      col.forEach((item, i, arr) => {
        const prevItem = arr[i - 1] ?? null
        let top = "0px"

        if (prevItem) {
          const prevHeight = prevItem.node.clientHeight
          const prevTop = prevItem.node.style.top
            ? parseInt(prevItem.node.style.top)
            : 0
          top = `${prevTop + prevHeight}px`
        }

        item.node.style.top = top
      })
    })
  }

  private getColumnHeightDiff() {
    const colHeights = this.getColHeights(this.cols)
    if (!colHeights.length) return 0
    return Math.max(...colHeights) - Math.min(...colHeights)
  }

  private setContainerHeight() {
    // If balancing columns, use the tallest column height, otherwise you're probably parallaxing - so use the shortest
    let height = 0

    const colHeights = this.getColHeights(this.cols)

    if (colHeights.length) {
      height = this.balanceCols
        ? Math.max(...colHeights)
        : Math.min(...colHeights)
    }

    this.container.style.height = `${height}px`
  }

  private getColHeights(cols: TItem[][]) {
    return cols.map((col) => {
      return col.reduce((h, i) => h + i.height, 0)
    })
  }

  init() {
    this.items = this.getItems()
    this.cols = this.getCols()

    this.setItemPositions()
    this.setContainerHeight()

    this.onLayout?.(this.getColumnHeightDiff())
  }

  destroy() {
    this.resizeObserver.disconnect()
    this.mutationObserver.disconnect()

    this.cols?.flat().forEach((item) => item.node.style.removeProperty("top"))
    this.container?.style?.removeProperty("height")
  }

  private rafId: number | null = null

  private redraw = () => {
    if (this.rafId !== null) return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null

      this.items = this.getItems()
      this.cols = this.getCols()
      this.setItemPositions()
      this.setContainerHeight()

      this.onLayout?.(this.getColumnHeightDiff())
    })
  }
}

type MasonryProps = {
  containerRef: RefObject<HTMLElement | null>
  columns: number
  balanceCols?: boolean
}

export const useMasonryLayout = ({
  containerRef,
  columns,
  balanceCols,
}: MasonryProps) => {
  const isSm = useMatchMedia("sm")

  const [layoutVersion, setLayoutVersion] = useState(0)
  const [heightDiff, setHeightDiff] = useState(0)

  useEffect(() => {
    if (!containerRef.current || !isSm) return

    const masonry = new MasonryConstructor({
      container: containerRef.current,
      columns,
      balanceCols,
      onLayout: (diff) => {
        setLayoutVersion((version) => version + 1)
        setHeightDiff(diff)
      },
    })
    masonry.init()

    return () => {
      masonry.destroy()
    }
  }, [containerRef, columns, isSm, balanceCols])

  return { layoutVersion, heightDiff }
}
